import { ProofDay, ProofDaySummary, ProofNumber, PROOF_NUMBERS, PROOF_TITLES, ProofRunStatus } from '../../types/proofs';

const DAY_LABEL = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** `YYYY-MM-DD` → "19 Sept"; unparseable input is shown as-is. */
export function formatStripDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? date : DAY_LABEL.format(d);
}

/** Status → badge palette + short text; `none` is a run that never happened (before go-live, or the scheduler skipped). */
export function chipFor(status: ProofRunStatus | 'none'): { cls: string; text: string } {
  switch (status) {
    case 'PASS': return { cls: 'approved', text: 'PASS' };
    case 'FAIL': return { cls: 'rejected', text: 'FAIL' };
    case 'INCONCLUSIVE': return { cls: 'pending', text: 'INCONCL.' };
    default: return { cls: '', text: '—' };
  }
}

interface ProofStripProps {
  days: ProofDay[];
  selectedRunId: string | null;
  onSelect: (run: ProofDaySummary, proof: ProofNumber, date: string) => void;
}

/**
 * Spec 053 Flow E step 3 — the last 31 days as a strip: one column per Dubai date (oldest → newest),
 * one row per proof. Each chip carries its status as text and colour, an aria-label with the proof,
 * date and status, and opens the run's detail when clicked; a day with no run is an inert dash.
 */
export default function ProofStrip({ days, selectedRunId, onSelect }: ProofStripProps) {
  const ordered = [...days].sort((a, b) => a.date.localeCompare(b.date));
  if (ordered.length === 0) {
    return <div className="admin-empty-state"><p>No proof runs yet — the first nightly run happens at 03:00 Asia/Dubai after ledger go-live.</p></div>;
  }
  return (
    <div className="admin-table-wrapper">
      <table className="admin-table" data-testid="proof-strip" style={{ fontSize: '0.6875rem' }}>
        <thead>
          <tr>
            <th scope="col" style={{ whiteSpace: 'nowrap' }}>Proof</th>
            {ordered.map((d) => (
              <th key={d.date} scope="col" style={{ textAlign: 'center', whiteSpace: 'nowrap', padding: '0.375rem 0.25rem', fontWeight: 500 }}>
                {formatStripDay(d.date)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PROOF_NUMBERS.map((proof) => (
            <tr key={proof} data-testid={`proof-row-${proof}`}>
              <th scope="row" style={{ whiteSpace: 'nowrap', textAlign: 'left', fontWeight: 600 }}>
                {proof} · {PROOF_TITLES[proof]}
              </th>
              {ordered.map((d) => {
                const run = d.proofs?.[String(proof)] ?? null;
                const chip = chipFor(run?.status ?? 'none');
                const label = `Proof ${proof} ${PROOF_TITLES[proof]}, ${d.date}: ${run ? run.status : 'no run'}`;
                return (
                  <td key={d.date} style={{ textAlign: 'center', padding: '0.25rem' }}>
                    {run ? (
                      <button
                        type="button"
                        className={`admin-badge ${chip.cls}`}
                        aria-label={label}
                        aria-pressed={selectedRunId === run.runId}
                        data-testid="proof-chip"
                        data-status={run.status}
                        data-date={d.date}
                        data-proof={proof}
                        style={{
                          border: selectedRunId === run.runId ? '2px solid #030c54' : '2px solid transparent',
                          cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.625rem', lineHeight: 1.2, padding: '0.125rem 0.375rem',
                        }}
                        onClick={() => onSelect(run, proof, d.date)}
                      >
                        {chip.text}
                      </button>
                    ) : (
                      <span aria-label={label} title={label} data-testid="proof-chip" data-status="none" data-date={d.date} data-proof={proof}
                        style={{ color: 'var(--admin-text-muted)' }}>
                        {chip.text}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
